import React from 'react'
import { BodyText } from '../../branding/typography'
import styled from 'styled-components/macro'
import { Radio } from 'antd'
import { getPriceWithVat } from '../../utils/priceFunctions'
import { CloseOutlined, GiftOutlined, UndoOutlined } from '@ant-design/icons'

const ChecklistColumn = (setOrders, updateOrder, table) => {
  const handleCheckOrder = (event, order, value) => {
    const updatedOrder = {
      ...order,
      receipting: {
        ...order.receipting,
        changeRequestAction: value,
        supplierApproved: value !== 'reject_request', // true for approve_credit and replacement_sent, false for reject_request
      },
    }
    updateOrder(updatedOrder)

    // Always add/update the order in the handled orders list
    setOrders(prev => {
      const curOrder = prev.find(item => item._id === updatedOrder._id)
      if (curOrder) {
        return prev.map(item =>
          item._id === updatedOrder._id ? updatedOrder : item,
        )
      } else {
        return [...prev, updatedOrder]
      }
    })
  }

  const isApprovedTable = table === 'approved'

  return [
    {
      title: 'SKU',
      dataIndex: 'sku',
      key: 'sku',
      className: 'restrictShrink',
      render: (_, order) => (
        <BodyText>
          <b>
            {order.receipting.updatedSku && !isApprovedTable
              ? order.receipting.updatedSku
              : order.sku}
          </b>
        </BodyText>
      ),
    },
    {
      title: 'Qty',
      dataIndex: 'quantity',
      key: 'quantity',
      className: 'restrictShrink quantity-padding-left',
      render: (_, order) => (
        <BodyText>
          <b>
            {(order.receipting.updatedQuantity && !isApprovedTable) ||
            (order.receipting.updatedQuantity === 0 && !isApprovedTable)
              ? order.receipting.updatedQuantity
              : order.quantity}
          </b>
        </BodyText>
      ),
    },
    {
      title: 'Size',
      className: 'restrictShrink quantity-padding-left',
      render: (text, order) => (
        <BodyText>
          {order?.receipting?.updatedSize
            ? order.receipting.updatedSize
            : order?.size
            ? order.size
            : order?.product?.size
            ? order.product.size
            : ''}
        </BodyText>
      ),
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      className: 'restrictShrink',
      width: 140,
      render: (_, order) => (
        <BodyTextWrap>
          {order?.receipting?.updatedTitle
            ? order.receipting.updatedTitle
            : order?.title
            ? order.title
            : order?.product?.title
            ? order.product.title
            : ''}
        </BodyTextWrap>
      ),
    },
    {
      title: 'Price',
      dataIndex: 'OMSPrice',
      className: 'restrictShrink',
      key: 'OMSPrice',
      render: (text, order) => {
        const hasVat = order.hasVat
        const isUpdatePrice =
          !!order.receipting.updatedPrice && !isApprovedTable
        return !isUpdatePrice
          ? `£${getPriceWithVat(order)}${hasVat ? ' + VAT' : ''}`
          : `£${(order.receipting.updatedPrice / 100).toFixed(2)}${
              hasVat ? ' + VAT' : ''
            }`
      },
    },
    {
      title: 'UoM',
      dataIndex: 'UoM',
      className: 'restrictShrink',
      key: 'product.UoM',
      render: text => {
        return text
          ? text === 'Each'
            ? `Price ${text}`
            : `Price per ${text}`
          : ''
      },
    },
    {
      title: 'Notes',
      dataIndex: 'receipting.notes',
      className: 'restrictShrink',
      key: 'productNotes',
      width: 180,
      render: notes => <BodyText>{notes ? notes : ''}</BodyText>,
    },
    {
      title: 'Reason for change',
      dataIndex: 'receipting.error',
      className: 'restrictShrink',
      width: 140,
      render: error => <BodyText>{error}</BodyText>,
    },
    {
      title: 'Supplier Decision',
      key: 'supplierDecision',
      className: 'restrictShrink text-right',
      width: 200,
      render: (text, order) => {
        const { changeRequestAction } = order.receipting
        return (
          <DecisionContainer>
            {isApprovedTable ? (
              <DecisionStatus decision={changeRequestAction}>
                {getDecisionText(changeRequestAction)}
                {getDecisionIcon(changeRequestAction)}
              </DecisionStatus>
            ) : (
              <Radio.Group
                value={changeRequestAction}
                onChange={e =>
                  handleCheckOrder(e, { ...order }, e.target.value)
                }
                size='small'
              >
                <Radio.Button value='approve_credit'>
                  <UndoOutlined style={{ marginRight: 4 }} />
                  Approve Credit
                </Radio.Button>
                <Radio.Button value='replacement_sent'>
                  <GiftOutlined style={{ marginRight: 4 }} />
                  Replacement
                </Radio.Button>
                <Radio.Button value='reject_request'>
                  <CloseOutlined style={{ marginRight: 4 }} />
                  Reject
                </Radio.Button>
              </Radio.Group>
            )}
          </DecisionContainer>
        )
      },
    },
  ]
}

// Helper functions for decision display
const getDecisionText = decision => {
  switch (decision) {
    case 'approve_credit':
      return 'Approve Credit (Refund Product)'
    case 'replacement_sent':
      return 'Replacement Product Sent'
    case 'reject_request':
      return 'Reject Request'
    default:
      return 'No Decision'
  }
}

const getDecisionIcon = decision => {
  switch (decision) {
    case 'approve_credit':
      return <UndoOutlined style={{ marginLeft: 8 }} />
    case 'replacement_sent':
      return <GiftOutlined style={{ marginLeft: 8 }} />
    case 'reject_request':
      return <CloseOutlined style={{ marginLeft: 8 }} />
    default:
      return null
  }
}

const BodyTextWrap = styled(BodyText)`
  white-space: break-spaces;
`

const DecisionContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: 100%;

  .ant-radio-group {
    display: flex;
    flex-direction: column;
    gap: 4px;
    width: 100%;
  }

  .ant-radio-button-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 4px 8px;
    font-size: 12px;
    height: auto;
    min-height: 32px;
    border-radius: 4px;

    &:first-child {
      border-radius: 4px 4px 0 0;
    }

    &:last-child {
      border-radius: 0 0 4px 4px;
    }

    &:not(:first-child) {
      border-top: 1px solid #d9d9d9;
    }
  }
`

const DecisionStatus = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 12px;
  border-radius: 4px;
  font-weight: 500;
  font-size: 12px;
  min-height: 32px;
  width: 100%;
  color: ${props => {
    switch (props.decision) {
      case 'approve_credit':
        return props.theme.colours.lettuceGreen
      case 'replacement_sent':
        return props.theme.colours.lettuceGreen
      case 'reject_request':
        return props.theme.colours.chilliRed
      default:
        return '#666'
    }
  }};
  background-color: ${props => {
    switch (props.decision) {
      case 'approve_credit':
        return `${props.theme.colours.lettuceGreen}15`
      case 'replacement_sent':
        return `${props.theme.colours.lettuceGreen}15`
      case 'reject_request':
        return `${props.theme.colours.chilliRed}15`
      default:
        return '#f5f5f5'
    }
  }};
  border: 1px solid
    ${props => {
      switch (props.decision) {
        case 'approve_credit':
          return props.theme.colours.lettuceGreen
        case 'replacement_sent':
          return props.theme.colours.lettuceGreen
        case 'reject_request':
          return props.theme.colours.chilliRed
        default:
          return '#d9d9d9'
      }
    }};
`

export default ChecklistColumn
